import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ROUTES, generatePath } from '@/constants/routes'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import {
  BrainCircuit,
  BookOpen,
  Calendar,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  Loader2,
} from "lucide-react"
import {
  getDiagnosis,
  getDailyTasks,
  listCourses,
  type DiagnosisResult,
  type DailyTasksResponse,
  type CourseListItem,
} from "../../api/aiTeam"

export default function AITeamDashboard() {
  const [diagnosis, setDiagnosis] = useState<DiagnosisResult | null>(null)
  const [dailyTasks, setDailyTasks] = useState<DailyTasksResponse | null>(null)
  const [courses, setCourses] = useState<CourseListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    try {
      const [diag, tasks, courseList] = await Promise.all([
        getDiagnosis().catch(() => null),
        getDailyTasks().catch(() => null),
        listCourses().catch(() => []),
      ])
      setDiagnosis(diag)
      setDailyTasks(tasks)
      setCourses(courseList)
    } catch (err) {
      console.error("Failed to load AI team data:", err)
    } finally {
      setLoading(false)
    }
  }

  const levelLabels = {
    weak: "待提升",
    medium: "中等",
    strong: "优秀",
  }

  const levelColors = {
    weak: "text-red-600 bg-red-50",
    medium: "text-yellow-600 bg-yellow-50",
    strong: "text-green-600 bg-green-50",
  }

  const TrendIcon = ({ trend }: { trend: string }) => {
    if (trend === "improving") return <TrendingUp className="w-4 h-4 text-green-500" />
    if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />
    return <Minus className="w-4 h-4 text-slate-400" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
          <BrainCircuit className="w-8 h-8 text-blue-600" />
          AI 学习中心
        </h1>
        <p className="text-slate-500 mt-2">你的专属 AI 辅导团队</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Diagnosis Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              学习诊断
            </CardTitle>
            <CardDescription>你的学习水平评估</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosis ? (
              <div className="space-y-4">
                <div className="text-center">
                  <span
                    className={`inline-block px-4 py-2 rounded-full text-lg font-semibold ${
                      levelColors[diagnosis.result.overallLevel as keyof typeof levelColors] ?? levelColors.medium
                    }`}
                  >
                    {levelLabels[diagnosis.result.overallLevel as keyof typeof levelLabels] ?? "中等"}
                  </span>
                </div>

                {diagnosis.result.subjectBreakdown?.length > 0 && (
                  <div className="space-y-2">
                    {diagnosis.result.subjectBreakdown.map((s) => (
                      <div
                        key={s.subject}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-slate-600">{s.subject}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{s.score}分</span>
                          <TrendIcon trend={s.trend} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {diagnosis.result.weakPoints?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-1">
                      薄弱知识点:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {diagnosis.result.weakPoints.slice(0, 3).map((wp) => (
                        <span
                          key={wp.kpCode}
                          className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded"
                        >
                          {wp.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-500 mb-4">还没有诊断记录</p>
                <Link to={ROUTES.AI_TEAM_DIAGNOSIS}>
                  <Button size="sm">开始诊断</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Tasks Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-green-500" />
              今日任务
            </CardTitle>
            <CardDescription>
              {dailyTasks?.date ?? new Date().toISOString().split("T")[0]}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dailyTasks?.tasks?.length ? (
              <div className="space-y-2">
                {dailyTasks.tasks.slice(0, 5).map((task, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 text-sm p-2 rounded-lg bg-slate-50"
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        task.status === "done"
                          ? "bg-green-500 border-green-500"
                          : "border-slate-300"
                      }`}
                    >
                      {task.status === "done" && (
                        <span className="text-white text-xs">✓</span>
                      )}
                    </div>
                    <span
                      className={
                        task.status === "done"
                          ? "line-through text-slate-400"
                          : "text-slate-700"
                      }
                    >
                      {task.title}
                    </span>
                    <span className="ml-auto text-xs text-slate-400">
                      {task.duration}分钟
                    </span>
                  </div>
                ))}
                <Link to={ROUTES.AI_TEAM_TASKS}>
                  <Button variant="ghost" size="sm" className="w-full mt-2">
                    查看全部 <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-500 mb-4">还没有今日任务</p>
                <Link to={ROUTES.AI_TEAM_TASKS}>
                  <Button size="sm">生成任务</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Courses Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5 text-purple-500" />
              我的课程
            </CardTitle>
            <CardDescription>
              {courses.length > 0
                ? `${courses.filter((c) => c.status === "in_progress").length} 门进行中`
                : "还没有课程"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {courses.length > 0 ? (
              <div className="space-y-3">
                {courses.slice(0, 3).map((course) => (
                  <Link
                    key={course.id}
                    to={generatePath(ROUTES.AI_TEAM_COURSE_DETAIL, { courseId: course.id })}
                    className="block p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-slate-700">
                        {course.title}
                      </span>
                      <span className="text-xs text-slate-400">
                        {course.completed_lessons}/{course.total_lessons}节
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: `${
                            course.total_lessons > 0
                              ? (course.completed_lessons / course.total_lessons) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </Link>
                ))}
                <Link to={ROUTES.AI_TEAM_COURSES}>
                  <Button variant="ghost" size="sm" className="w-full">
                    查看全部 <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-500 mb-4">还没有课程</p>
                <Link to={ROUTES.AI_TEAM_COURSES}>
                  <Button size="sm">浏览课程</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to={ROUTES.AI_TEAM_DIAGNOSIS}>
          <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
            <AlertCircle className="w-6 h-6" />
            <span>学习诊断</span>
          </Button>
        </Link>
        <Link to={ROUTES.AI_TEAM_COURSES}>
          <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
            <BookOpen className="w-6 h-6" />
            <span>我的课程</span>
          </Button>
        </Link>
        <Link to={ROUTES.MISTAKES}>
          <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
            <AlertCircle className="w-6 h-6" />
            <span>错题本</span>
          </Button>
        </Link>
        <Link to={ROUTES.AI_TEAM_TASKS}>
          <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
            <Calendar className="w-6 h-6" />
            <span>每日任务</span>
          </Button>
        </Link>
      </div>
    </div>
  )
}
